import client from 'api/client'
import { DOC_PATH } from 'paths'
import { EditorView } from 'prosemirror-view'
import { Fragment } from 'prosemirror-model'
import { generatePath } from 'react-router-dom'
import { handleErrorResponseWithMessage } from 'api/utils/handleErrorResponseWithMessage'
import { useModals } from 'almanac-ui/Modal'
import { ReplaceStep, Step } from 'prosemirror-transform'
import { useState } from 'react'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import * as Yup from 'yup'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { TextChannels } from '../../../../../../constants'
import { ArticleMeta } from 'types'
import { getDocumentFromJson } from 'proseShared/utils/state'
import { schema } from 'prose-core'
import { Node as ProseNode } from 'prosemirror-model'
import { usePostProperties } from 'components/ArticlePostPreview/usePostProperties'
import { AssigneeType } from 'api/clients/PropertyClient'
import { useCurrentArticleMeta } from 'hooks/useCurrentArticleMeta'

interface Props {
  view?: EditorView
  articleChannel?: ChannelType
  scheduledPost?: ScheduledPostShow
  onSubmit?: () => void
  article?: ArticleMeta & { scheduled_post?: ScheduledPostShow }
}

const generateReplicationSteps = (doc: ProseNode): Step[] => {
  // Create a ReplaceStep that replaces the entire content of the empty document with the existing document's content
  const step = new ReplaceStep(0, 0, doc.slice(0, doc.content.size))

  return [step]
}

export const useCrossPost = ({
  view,
  articleChannel,
  scheduledPost: initialScheduledPost,
  onSubmit,
  article,
}: Props) => {
  const workspace = useCurrentWorkspace()
  const { closeAllModals } = useModals()
  const articleMeta = useCurrentArticleMeta()
  const { postAt } = usePostProperties({ id: articleMeta?.id as string, type: AssigneeType.Article })

  const scheduledPost = initialScheduledPost || article?.scheduled_post

  const selectionChannels = [
    { title: TextChannels[ChannelType.InstagramPosts].name, key: ChannelType.InstagramPosts },
    { title: TextChannels[ChannelType.FacebookPost].name, key: ChannelType.FacebookPost },
    { title: TextChannels[ChannelType.Tweets].name, key: ChannelType.Tweets },
    { title: TextChannels[ChannelType.BlogPost].name, key: ChannelType.BlogPost },
    { title: TextChannels[ChannelType.LinkedInPosts].name, key: ChannelType.LinkedInPosts },
  ]

  const channelKeys = selectionChannels.map((channel) => channel.key)

  const [channel, setChannel] = useState(() => {
    if (articleChannel && channelKeys.includes(articleChannel)) {
      return articleChannel
    } else {
      return ChannelType.InstagramPosts
    }
  })

  const handleSubmit = async (values: { filename: string; project: any }) => {
    await turnTextIntoDoc(values.filename, values.project)
  }

  const generateSteps = (view: EditorView): Step[] => {
    const {
      state,
      state: { selection },
    } = view
    const tr = state.tr
    let content = Fragment.empty

    if (!selection.empty) content = state.selection.content().content

    tr.insert(0, content)
    return tr.steps
  }

  const generateStepsFromDoc = (): Step[] => {
    if (!article?.main_layer) return []
    const doc = getDocumentFromJson(article?.main_layer.elements, schema)
    return generateReplicationSteps(doc)
  }

  const turnTextIntoDoc = async (title: string, chosenFolder: any) => {
    if (!workspace?.key) {
      return
    }
    closeAllModals()
    onSubmit?.()
    let steps
    if (view) {
      steps = generateSteps(view)
    } else if (article?.main_layer) {
      steps = generateStepsFromDoc()
    }

    const dismissToast = showToastMessage('Generating your Content...', 'loading', undefined, 0)
    const response = await client.createArticle(
      {
        project: chosenFolder?.project_id,
        title: title,
        channel: channel,
      },
      workspace?.key,
      false,
      undefined,
      steps,
      undefined,
      scheduledPost?.media_objects.sort((a, b) => a.position - b.position)?.map((media) => media.id),
      scheduledPost?.content,
      postAt,
    )

    if (response?.type === SUCCESS_RESPONSE_TYPE) {
      dismissToast()
      showToastMessage(
        'Post generated successfully!',
        SUCCESS_RESPONSE_TYPE,
        {
          variant: 'proceed',
          label: 'Open',
          type: 'link',
          to: generatePath(DOC_PATH, { slug: response.data.article.slug }),
        },
        10,
      )
    } else {
      handleErrorResponseWithMessage()
    }
  }

  const validationSchema = Yup.object().shape({
    filename: Yup.string().required('Title is required'),
    project: Yup.object().required('Project is required'),
  })

  return {
    handleSubmit,
    validationSchema,
    channel,
    setChannel,
    selectionChannels,
  }
}
